import React from 'react';
import { UsersRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import {
    chronicRejectedGroups,
    type CampaignFailureBreakdown,
} from '@common-components/message-status/campaign-failure-breakdown';

/** Groups named inline before collapsing the rest into "+N more". */
const MAX_VISIBLE_GROUPS = 5;

interface RejectedGroupsRowProps {
    breakdown: CampaignFailureBreakdown | undefined | null;
}

/**
 * BE#613 — report row "Groups WhatsApp rejected": the groups whose sends WhatsApp itself refused
 * (per-recipient ack error) AND that also failed on the owner's previous send, so it is a pattern
 * (left the group / can't post), not a blip. Data comes from the server-side breakdown.
 */
export const RejectedGroupsRow: React.FC<RejectedGroupsRowProps> = ({ breakdown }) => {
    const { t } = useTranslation(namespace);
    const groups = chronicRejectedGroups(breakdown);
    if (groups.length === 0) return null;

    const visible = groups.slice(0, MAX_VISIBLE_GROUPS);
    const hidden = groups.length - visible.length;

    return (
        <div
            role="status"
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '12px 14px',
                marginBottom: 14,
                borderRadius: 10,
                border: '1px solid var(--line)',
                background: 'var(--surface)',
                borderInlineStart: '3px solid #ef4444',
            }}
        >
            <UsersRound size={18} style={{ color: '#ef4444', flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.45 }}>
                    {t('campaignProgress.rejectedGroupsTitle', { count: groups.length })}
                </p>
                <p style={{ margin: '2px 0 6px', fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>
                    {t('campaignProgress.rejectedGroupsHint')}
                </p>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 13, color: 'var(--ink)' }}>
                    {visible.map((g) => (
                        <li key={g.chatId} style={{ display: 'flex', gap: 8, lineHeight: 1.6 }}>
                            <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{g.name || g.chatId}</span>
                            <span className="num" style={{ color: 'var(--muted)', flexShrink: 0 }}>×{g.count}</span>
                        </li>
                    ))}
                </ul>
                {hidden > 0 && (
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted)' }}>
                        {t('campaignProgress.rejectedGroupsMore', { count: hidden })}
                    </p>
                )}
            </div>
        </div>
    );
};

export default RejectedGroupsRow;
